// components/Calendar/CustomDateTimePicker.jsx
import React, { useState, useEffect, useRef } from 'react';
import { Calendar, Clock, ChevronLeft, ChevronRight } from 'lucide-react';
import './styles/CustomDateTimePicker.css';

const CustomDateTimePicker = ({
  value,
  onChange,
  useMilitaryTime = false,
  label = "Select Date & Time"
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedHour, setSelectedHour] = useState(9);
  const [selectedMinute, setSelectedMinute] = useState(0);
  const [viewDate, setViewDate] = useState(new Date());
  const pickerRef = useRef(null);

  // Parse initial value
  useEffect(() => {
    if (value) {
      const date = new Date(value);
      setSelectedDate(date);
      setSelectedHour(date.getHours());
      setSelectedMinute(date.getMinutes());
      setViewDate(date);
    }
  }, [value]);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const formatDisplayValue = () => {
    if (!selectedDate) return '';

    const dateStr = selectedDate.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });

    if (useMilitaryTime) {
      const hourStr = String(selectedHour).padStart(2, '0');
      const minStr = String(selectedMinute).padStart(2, '0');
      return `${dateStr} ${hourStr}:${minStr}`;
    } else {
      const hour12 = selectedHour === 0 ? 12 : selectedHour > 12 ? selectedHour - 12 : selectedHour;
      const ampm = selectedHour < 12 ? 'AM' : 'PM';
      const minStr = String(selectedMinute).padStart(2, '0');
      return `${dateStr} ${hour12}:${minStr} ${ampm}`;
    }
  };

  const handleDateSelect = (day) => {
    const newDate = new Date(viewDate.getFullYear(), viewDate.getMonth(), day);
    newDate.setHours(selectedHour, selectedMinute, 0, 0);
    setSelectedDate(newDate);

    // Convert to YYYY-MM-DDTHH:mm format
    const year = newDate.getFullYear();
    const month = String(newDate.getMonth() + 1).padStart(2, '0');
    const dayStr = String(newDate.getDate()).padStart(2, '0');
    const hourStr = String(selectedHour).padStart(2, '0');
    const minStr = String(selectedMinute).padStart(2, '0');

    onChange(`${year}-${month}-${dayStr}T${hourStr}:${minStr}`);
  };

  const handleTimeChange = (newHour, newMinute) => {
    setSelectedHour(newHour);
    setSelectedMinute(newMinute);

    if (selectedDate) {
      const newDate = new Date(selectedDate);
      newDate.setHours(newHour, newMinute, 0, 0);

      const year = newDate.getFullYear();
      const month = String(newDate.getMonth() + 1).padStart(2, '0');
      const day = String(newDate.getDate()).padStart(2, '0');
      const hourStr = String(newHour).padStart(2, '0');
      const minStr = String(newMinute).padStart(2, '0');

      onChange(`${year}-${month}-${day}T${hourStr}:${minStr}`);
    }
  };

  const getDaysInMonth = () => {
    const year = viewDate.getFullYear();
    const month = viewDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];

    // Add empty cells for days before the first of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }

    // Add all days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(day);
    }

    return days;
  };

  const previousMonth = () => {
    setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 1));
  };

  const generateHourOptions = () => {
    if (useMilitaryTime) {
      return Array.from({ length: 24 }, (_, i) => i);
    } else {
      return Array.from({ length: 12 }, (_, i) => i + 1);
    }
  };

  const generateMinuteOptions = () => {
    return [0, 15, 30, 45];
  };

  const formatHourDisplay = (hour) => {
    if (useMilitaryTime) {
      return String(hour).padStart(2, '0');
    } else {
      return String(hour);
    }
  };

  const convertTo24Hour = (hour12, isPM) => {
    if (hour12 === 12) {
      return isPM ? 12 : 0;
    }
    return isPM ? hour12 + 12 : hour12;
  };

  const isToday = (day) => {
    if (!day) return false;
    const today = new Date();
    return day === today.getDate() &&
           viewDate.getMonth() === today.getMonth() &&
           viewDate.getFullYear() === today.getFullYear();
  };

  const isSelected = (day) => {
    if (!day || !selectedDate) return false;
    return day === selectedDate.getDate() &&
           viewDate.getMonth() === selectedDate.getMonth() &&
           viewDate.getFullYear() === selectedDate.getFullYear();
  };

  return (
    <div className="custom-datetime-picker" ref={pickerRef}>
      <div className="picker-input" onClick={() => setIsOpen(!isOpen)}>
        <Calendar size={16} />
        <span className="picker-value">{formatDisplayValue() || 'Select date and time'}</span>
        <Clock size={16} />
      </div>

      {isOpen && (
        <div className="picker-dropdown">
          <div className="picker-content">
            {/* Calendar Section */}
            <div className="picker-calendar">
            <div className="calendar-header">
              <button type="button" onClick={previousMonth} className="nav-button">
                <ChevronLeft size={16} />
              </button>
              <span className="month-year">
                {viewDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
              </span>
              <button type="button" onClick={nextMonth} className="nav-button">
                <ChevronRight size={16} />
              </button>
            </div>

            <div className="calendar-grid">
              <div className="weekday-header">
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                  <div key={day} className="weekday">{day}</div>
                ))}
              </div>
              <div className="days-grid">
                {getDaysInMonth().map((day, index) => (
                  <div
                    key={index}
                    className={`day-cell ${!day ? 'empty' : ''} ${isToday(day) ? 'today' : ''} ${isSelected(day) ? 'selected' : ''}`}
                    onClick={() => day && handleDateSelect(day)}
                  >
                    {day}
                  </div>
                ))}
              </div>
            </div>
                     <div className="time-selectors">
              {useMilitaryTime ? (
                <>
                  {/* 24-hour format */}
                  <div className="time-column-dp">
                    <select
                      value={selectedHour}
                      onChange={(e) => handleTimeChange(parseInt(e.target.value), selectedMinute)}
                      className="time-select"
                    >
                      {generateHourOptions().map(hour => (
                        <option key={hour} value={hour}>
                          {formatHourDisplay(hour)}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="time-separator">:</div>
                  <div className="time-column-dp">
                    <select
                      value={selectedMinute}
                      onChange={(e) => handleTimeChange(selectedHour, parseInt(e.target.value))}
                      className="time-select"
                    >
                      {generateMinuteOptions().map(min => (
                        <option key={min} value={min}>
                          {String(min).padStart(2, '0')}
                        </option>
                      ))}
                    </select>
                  </div>
                </>
              ) : (
                <>
                  {/* 12-hour format with AM/PM */}
                  <div className="time-column-dp">
                    <select
                      value={selectedHour > 12 ? selectedHour - 12 : (selectedHour === 0 ? 12 : selectedHour)}
                      onChange={(e) => {
                        const hour12 = parseInt(e.target.value);
                        const isPM = selectedHour >= 12;
                        const hour24 = convertTo24Hour(hour12, isPM);
                        handleTimeChange(hour24, selectedMinute);
                      }}
                      className="time-select"
                    >
                      {generateHourOptions().map(hour => (
                        <option key={hour} value={hour}>
                          {hour}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="time-separator">:</div>
                  <div className="time-column-dp">
                    <select
                      value={selectedMinute}
                      onChange={(e) => handleTimeChange(selectedHour, parseInt(e.target.value))}
                      className="time-select"
                    >
                      {generateMinuteOptions().map(min => (
                        <option key={min} value={min}>
                          {String(min).padStart(2, '0')}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="time-column-dp">
                    <select
                      value={selectedHour >= 12 ? 'PM' : 'AM'}
                      onChange={(e) => {
                        const isPM = e.target.value === 'PM';
                        const hour12 = selectedHour > 12 ? selectedHour - 12 : (selectedHour === 0 ? 12 : selectedHour);
                        const hour24 = convertTo24Hour(hour12, isPM);
                        handleTimeChange(hour24, selectedMinute);
                      }}
                      className="time-select"
                    >
                      <option value="AM">AM</option>
                      <option value="PM">PM</option>
                    </select>
                  </div>
                </>
              )}
            </div>

          </div>

          
          </div>

          <div className="picker-actions">
            
              <div className="time-preview">
              Selected: {formatDisplayValue() || 'No time selected'}
            </div>
            <button type="button" onClick={() => setIsOpen(false)} className="btn-done">
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomDateTimePicker;
